// Display-only helpers for the Intertown booking screen. The server's quote is
// always the authority on what is charged; these only let the screen show a
// price before the quote call has answered.

export const formatRupees = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;

export const placeLabel = (place) => place?.name || place?.address || '';

/**
 * The fare a route shows for a trip type, mirroring buildQuote in
 * firebase/functions/intertown.js: an admin fixed fare when set, otherwise the
 * per-km rate over the (doubled, for a return) distance rounded to 0.1 km.
 * Returns { fare, mode } with mode 'fixed' | 'per_km', or nulls when unknown.
 */
export function routeFare(route, tripType, perKm) {
  if (!route) return { fare: null, mode: null };
  const isReturn = tripType === 'return';
  const fixed = Number(isReturn ? route.fixedReturnFare : route.fixedOneWayFare);
  if (Number.isFinite(fixed) && fixed > 0) return { fare: Math.round(fixed), mode: 'fixed' };
  const rate = Number(perKm);
  const distance = Number(route.distanceKm);
  if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(distance) || distance <= 0) {
    return { fare: null, mode: null };
  }
  const billedKm = Math.round(distance * (isReturn ? 2 : 1) * 10) / 10;
  return { fare: Math.round(billedKm * rate), mode: 'per_km' };
}

// ── Place labels ────────────────────────────────────────────────────────────
// Google often names a rural pin only by its plot number ("14/50") or by a plus
// code ("MW3R+Q9X"), and starts the address with that plus code. Riders see
// these on their trip, and Annas see them on the open-offer card, so tidy them
// when a place is picked. Only used by the Intertown picker: the shared geocoder
// that Book relies on is left alone.

// Open Location Code (plus code) alphabet.
const OLC = '23456789CFGHJMPQRVWX';
const PLUS_CODE_LEAD = new RegExp(`^[${OLC}]{4,8}\\+[${OLC}]{2,}\\s*`, 'i');
const PLUS_CODE_ONLY = new RegExp(`^[${OLC}]{4,8}\\+[${OLC}]{2,}(\\s.*)?$`, 'i');
const hasLetters = (text) => /\p{L}/u.test(text);

/** An address split into readable parts: no leading plus code, no trailing country. */
export function addressParts(address) {
  const parts = String(address || '').split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length) parts[0] = parts[0].replace(PLUS_CODE_LEAD, '').trim();
  const readable = parts.filter(Boolean);
  if (readable.length && /^india$/i.test(readable[readable.length - 1])) readable.pop();
  return readable;
}

/** A picked place with a readable name and address. Never leaves either empty. */
export function tidyPlace(place) {
  if (!place || typeof place !== 'object') return place;
  const parts = addressParts(place.address);
  const address = parts.length ? parts.join(', ') : place.address;
  let name = String(place.name || '').trim();
  if (!name || PLUS_CODE_ONLY.test(name)) {
    const readable = parts.filter(hasLetters);
    if (readable.length) name = readable.slice(0, 2).join(', ');
  } else if (!hasLetters(name)) {
    const area = parts.find((part) => hasLetters(part) && part !== name);
    if (area) name = `${name}, ${area}`;
  }
  return { ...place, name: name || place.name, address };
}

// ── Anna's live location ────────────────────────────────────────────────────
// A parked phone only reports in about every 45 s (the Anna app's heartbeat), so
// "live" has to tolerate at least two missed beats. Local rides already use the
// platform's captainLocationMaxAgeSec (180 s by default) for the same reason.
export const MIN_LIVE_LOCATION_AGE_MS = 90 * 1000;

export function liveLocationMaxAgeMs(captainLocationMaxAgeSec) {
  const configured = Number(captainLocationMaxAgeSec);
  return Math.max(MIN_LIVE_LOCATION_AGE_MS, (Number.isFinite(configured) && configured > 0 ? configured : 180) * 1000);
}

/** 'waiting' = no location received yet, 'live' = recent, 'stale' = went quiet. */
export function locationFreshness({ updatedAt, now, maxAgeMs }) {
  const at = Number(updatedAt);
  if (!Number.isFinite(at) || at <= 0) return 'waiting';
  return now - at < maxAgeMs ? 'live' : 'stale';
}

/**
 * What the live-map strip shows for a trip status and Anna's last location node
 * (rideLocations/{id}). The server refreshes the ETA only while Anna heads to the
 * pickup and to the drop: at the pickup it just reads 0, and at the destination or
 * on the way back it is a leftover from the drop leg. So only 'confirmed' and
 * 'started' get an ETA. Returns { freshness, atPickup, etaMin } (etaMin null = hide).
 */
export function liveTripStrip({ status, driverLocation, now, maxAgeMs }) {
  const freshness = locationFreshness({ updatedAt: driverLocation?.updatedAt, now, maxAgeMs });
  const raw = driverLocation?.etaMin;
  const eta = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  const etaApplies = status === 'confirmed' || status === 'started';
  return {
    freshness,
    atPickup: status === 'arrived_at_pickup',
    etaMin: freshness === 'live' && etaApplies && Number.isFinite(eta) && eta >= 0 ? eta : null,
  };
}
